import React from 'react';
import { ChevronRight, AlertCircle, CheckCircle, HelpCircle, Loader } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { cn } from '../../utils/cn';
import { parseReferences } from '../../utils/referenceExtractor';

const StatusIcon = ({ status }) => {
    switch (status) {
        case 'success': 
        case 'completed': 
            return <CheckCircle className="h-5 w-5 text-success-500" />;
        case 'processing': 
            return <Loader className="h-5 w-5 text-primary-500 animate-spin" />;
        case 'pending': 
            return <HelpCircle className="h-5 w-5 text-slate-300" />;
        case 'warning': 
            return <AlertCircle className="h-5 w-5 text-warning-500" />;
        case 'danger': 
            return <AlertCircle className="h-5 w-5 text-danger-500" />;
        default: 
            return <HelpCircle className="h-5 w-5 text-slate-400" />;
    }
};

export const QuestionCard = ({
    number,
    question,
    answer,
    reference,
    onClick,
    status = 'default'
}) => {
    // Extract answer text if it's an array
    const answerText = Array.isArray(answer) ? answer[0] : answer;
    const sectionRef = reference || (Array.isArray(answer) && answer[1] ? answer[1] : null);
    
    // Parse multiple references if they exist
    const references = parseReferences(sectionRef);

    // Determine if card should be interactive based on status
    const isInteractive = status === 'completed' || status === 'success' || (status === 'default' && answer);
    const isPending = status === 'pending';
    const isProcessing = status === 'processing';

    return (
        <Card
            interactive={isInteractive}
            onClick={isInteractive ? onClick : undefined}
            className={cn(
                "group relative overflow-hidden transition-all duration-300 h-64",
                isPending && "opacity-50 cursor-not-allowed",
                isProcessing && "ring-2 ring-primary-300 animate-pulse"
            )}
        >
            <div className={cn(
                "absolute top-0 left-0 w-1 h-full transition-all duration-200",
                isProcessing && "bg-primary-500 opacity-100",
                isPending && "bg-slate-300 opacity-50",
                (status === 'completed' || status === 'success') && "bg-success-500 opacity-0 group-hover:opacity-100",
                status === 'default' && answer && "bg-primary-500 opacity-0 group-hover:opacity-100"
            )} />

            <CardContent className="flex flex-col gap-4 h-full">
                <div className="flex gap-4 flex-shrink-0">
                    <div className="flex-shrink-0">
                        <div className={cn(
                            "h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-200",
                            isPending && "bg-slate-100 text-slate-400",
                            isProcessing && "bg-primary-100 text-primary-600",
                            (status === 'completed' || status === 'success') && "bg-success-50 text-success-600",
                            status === 'default' && answer && "bg-primary-50 text-primary-600"
                        )}>
                            {isProcessing ? <Loader className="h-4 w-4 animate-spin" /> : number}
                        </div>
                    </div>

                    <div className="flex-1 min-w-0">
                        <h3 className={cn(
                            "font-semibold leading-tight transition-colors duration-200",
                            isPending ? "text-slate-400" : "text-slate-900"
                        )}>
                            {question}
                        </h3>
                    </div>

                    {isInteractive && (
                        <div className="flex-shrink-0 self-start opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
                            <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                    )}
                    
                    {(isPending || isProcessing) && (
                        <div className="flex-shrink-0 self-start">
                            <StatusIcon status={status} />
                        </div>
                    )}
                </div>

                <div className="flex-1 min-h-0 overflow-hidden">
                    {isProcessing && (
                        <div className="flex items-center gap-2 text-primary-600 text-sm">
                            <Loader className="h-4 w-4 animate-spin" />
                            <span className="font-medium">Processing answer...</span>
                        </div>
                    )}
                    
                    {isPending && (
                        <p className="text-slate-400 text-sm italic">
                            Waiting to be processed...
                        </p>
                    )}
                    
                    {answerText && !isPending && !isProcessing && (
                        <div className="overflow-hidden h-full">
                            <p className="text-slate-600 text-sm line-clamp-5">
                                {answerText}
                            </p>
                        </div>
                    )}
                </div>

                {answerText && !isPending && !isProcessing && (
                    <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
                        <Badge variant="primary" className="group-hover:bg-primary-600 group-hover:text-white transition-colors">
                            Deep Dive Available
                        </Badge>
                        {references.length > 0 && references.map((ref, idx) => (
                            <Badge 
                                key={idx} 
                                variant="secondary" 
                                className="text-xs"
                            >
                                📄 {ref}
                            </Badge>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
